import React, { useEffect, useRef } from "react";
import { useMoodleV2 } from "../../../contexts/moodle";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Tooltip from "@mui/material/Tooltip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/config";
import { requestAutoLogin } from "../../../services/moodle/userauth";
import { getCourseCertificate } from "../../../services/moodle/courses";
import * as d3 from "d3";

// cambio
export const ProgressRowItem = ({ course, grade, progress, lang, includeEncuesta, certificateCmid, rowIndex, onRowClick }) => {
  const moodlev2 = useMoodleV2();
  const theme = useTheme();
  const currentRequestId = useRef(0);

  const [data /* setData */] = React.useState({
    name: course.displayname,
    start: new Date(course.startdate * 1000).toLocaleDateString(),
    end: course.enddate !== 0 ? new Date(course.enddate * 1000).toLocaleDateString() : "Sin fecha de termino",
  });
  const { user } = useAuth();
  const { config } = useConfig();

  const [downloadStatus, setDownloadStatus] = React.useState("idle"); // "idle" | "loading" | "success"
  const successTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const isEven = rowIndex % 2 === 1;

  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || progress === undefined || progress === null) return;
    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const size = 36;
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const arc = d3.arc().innerRadius(radius - strokeWidth).outerRadius(radius);
    const pie = d3.pie().value((d) => d.value).sort(null);
    const data = [
      { label: "done", value: progress },
      { label: "pending", value: 100 - progress },
    ];

    // Originalmente los colores provistos por la API de WordPress (background_completado y background_pendiente)
    // no presentaban buen contraste al coexistir en este indicador de 36px. Por decision del cliente,
    // se preserva el color de completado dinamico de la API/tema y se utiliza un gris neutro como pista (track)
    // para asegurar contraste optimo y legibilidad del avance.
    const completedColor =
      theme.mode === "dark"
        ? (theme.colors.backgroundCompletadoDark || theme.colors.button_dark || "#40ac3d")
        : (theme.colors.backgroundCompletadoLight || theme.colors.button_light || "#40ac3d");

    const trackColor =
      theme.mode === "dark"
        ? "rgba(255, 255, 255, 0.12)"
        : "#E5E7EB";

    const colors = d3.scaleOrdinal([completedColor, trackColor]);

    svg
      .attr("width", size)
      .attr("height", size)
      .append("g")
      .attr("transform", `translate(${size / 2},${size / 2})`)
      .selectAll("path")
      .data(pie(data))
      .join("path")
      .attr("d", arc)
      .attr("fill", (_, i) => colors(i));
  }, [progress, theme.mode, theme.colors]);

  const handleRowClick = () => {
    if (onRowClick) onRowClick(course);
  };

  const handleCourseNavigation = async (e) => {
    e.stopPropagation();
    if (!certificateCmid || downloadStatus !== "idle" || moodlev2.isDownloading) return;

    setDownloadStatus("loading");
    moodlev2.setIsDownloading(true);

    try {
      const res = await getCourseCertificate({
        token: user.token,
        cmid: certificateCmid,
        userid: user.id || 0,
        baseUrl: config.baseURL,
      });

      if (res && res.filecontent) {
        const byteCharacters = atob(res.filecontent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = res.filename || `Certificado_${course.displayname || "curso"}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);

        setDownloadStatus("success");
        successTimerRef.current = setTimeout(() => {
          setDownloadStatus("idle");
        }, 2000);
        return;
      }
    } catch (err) {
      console.warn("Direct certificate download not available or failed, using fallback:", err.message);
      try {
        let { key } = await requestAutoLogin({
          username: user.username,
          baseUrl: config.baseURL,
          tipddytoken: config.token,
        });
        const url = `https://api-interfaz-alumnos.tipddy.cl/history/cert/${certificateCmid}?userid=${user.id}&domain=${encodeURIComponent(config.baseURL)}&userkey=${key}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (fallbackErr) {
        console.error("Fallback download failed:", fallbackErr);
      }
      setDownloadStatus("idle");
    } finally {
      moodlev2.setIsDownloading(false);
    }
  };

  return (
    <>
      <tr
        onClick={handleRowClick}
        className="cursor-pointer transition-colors duration-150"
        style={{
          backgroundColor: isEven
            ? (theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F5F5F5")
            : (theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF"),
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#E8E8E8"}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isEven
          ? (theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F5F5F5")
          : (theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF")}
      >
        <th
          scope="row"
          className="p-[16px] font-lato font-medium text-[14px] border-none"
          style={{ color: theme.mode === "dark" ? "#E0E0E0" : "#1A1A1A" }}
        >
          <p
            className="font-lato font-medium text-[14px] break-words whitespace-normal leading-snug"
            style={{ color: theme.mode === "dark" ? "#E0E0E0" : "#1A1A1A" }}
          >
            {data.name}
          </p>
        </th>
        <td
          className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap"
          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D" }}
        >
          {progress !== undefined && progress !== null ? (
            <div className="flex items-center gap-2">
              <svg ref={chartRef} />
              <span>{Math.floor(progress)}%</span>
            </div>
          ) : "-"}
        </td>
        <td
          className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap"
          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D" }}
        >
          {grade !== "-" && progress !== null && progress !== undefined && progress < 100 ? (
            <div className="flex flex-col items-start gap-1">
              <span className="font-semibold">{grade}</span>
              <span
                className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: theme.mode === "dark" ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
                  color: theme.mode === "dark" ? "#FBBF24" : "#B45309",
                }}
              >
                En progreso
              </span>
            </div>
          ) : (
            <span>{grade}</span>
          )}
        </td>
        <td
          className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap"
          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D" }}
        >
          {data.start}
        </td>
        <td
          className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap"
          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D" }}
        >
          {data.end === "Sin fecha de termino" ? lang["History"].fourth_column_answer : data.end}
        </td>
        <td
          className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap z-50"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {certificateCmid ? (
            <button
              type="button"
              disabled={downloadStatus !== "idle" || moodlev2.isDownloading}
              onClick={(e) => {
                e.stopPropagation();
                if (downloadStatus === "idle" && !moodlev2.isDownloading) {
                  handleCourseNavigation(e);
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold transition-all duration-150 select-none ${
                downloadStatus === "loading"
                  ? "cursor-wait opacity-80"
                  : downloadStatus === "success"
                  ? "cursor-default"
                  : "cursor-pointer hover:shadow-sm hover:brightness-95 active:scale-95"
              }`}
              style={{
                backgroundColor:
                  downloadStatus === "success"
                    ? theme.mode === "dark"
                      ? "rgba(34, 197, 94, 0.25)"
                      : "#BBF7D0"
                    : theme.mode === "dark"
                    ? "rgba(34, 197, 94, 0.15)"
                    : "#DCFCE7",
                color:
                  theme.mode === "dark"
                    ? "#4ADE80"
                    : "#15803D",
                border:
                  theme.mode === "dark"
                    ? "1px solid rgba(74, 222, 128, 0.25)"
                    : "1px solid #BBF7D0",
              }}
            >
              {downloadStatus === "loading" && (
                <>
                  <CircularProgress
                    size={14}
                    thickness={5}
                    sx={{ color: theme.mode === "dark" ? "#4ADE80" : "#15803D" }}
                  />
                  <span>Generando...</span>
                </>
              )}
              {downloadStatus === "success" && (
                <>
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: theme.mode === "dark" ? "#4ADE80" : "#15803D", fontSize: "16px" }}
                  />
                  <span>¡Descargado!</span>
                </>
              )}
              {downloadStatus === "idle" && (
                <>
                  <DownloadIcon fontSize="small" sx={{ fontSize: "16px" }} />
                  <span>{lang["History"].fifth_column_answer}</span>
                </>
              )}
            </button>
          ) : (
            <Tooltip
              title={
                progress !== null && progress !== undefined && Number(progress) >= 100
                  ? "Este curso no cuenta con certificado emitido"
                  : "Disponible al completar el 100% del curso"
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "15px",
                    fontWeight: 500,
                    padding: "10px 16px",
                    borderRadius: "8px",
                    lineHeight: 1.4,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                  },
                },
                arrow: {
                  sx: {
                    fontSize: "16px",
                  },
                },
              }}
            >
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium cursor-help transition-all hover:opacity-80 select-none"
                style={{
                  backgroundColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#F3F4F6",
                  color: theme.mode === "dark" ? "#9CA3AF" : "#6B7280",
                  border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #E5E7EB",
                }}
              >
                <LockOutlinedIcon style={{ fontSize: "12px" }} />
                <span>Pendiente</span>
              </span>
            </Tooltip>
          )}
        </td>
        {includeEncuesta && (
          <td className="p-[16px] font-lato text-[14px] border-none whitespace-nowrap" style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#3D3D3D" }}>
            {" "}
            - {/* {lang["History"].sixth_column_answer} */}
          </td>
        )}
      </tr>
    </>
  );
};
